import React, { useState, useEffect } from 'react';
import { Save, Check } from 'lucide-react';
import { InventoryType, InventoryCategory } from '../../../types/inventory';
import { mockInventoryService } from '../../../services/mockInventoryService';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { confirmDelete, swalError } from '../../common/confirm';

interface InventoryTypesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  type_code: string;
  type_name: string;
  type_description: string;
  category_id: number;
  file_extensions: string;
  default_base_path: string;
  supports_versioning: boolean;
  supports_dependencies: boolean;
  display_order: number;
  is_active: boolean;
}

interface FormErrors {
  type_code?: string;
  type_name?: string;
  type_description?: string;
  category_id?: string;
  file_extensions?: string;
  default_base_path?: string;
  display_order?: string;
}

const initialFormData: FormData = {
  type_code: '',
  type_name: '',
  type_description: '',
  category_id: 0,
  file_extensions: '',
  default_base_path: '',
  supports_versioning: false,
  supports_dependencies: false,
  display_order: 1,
  is_active: true
};

const InventoryTypesModal: React.FC<InventoryTypesModalProps> = ({ isOpen, onClose }) => {
  const [types, setTypes] = useState<InventoryType[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<InventoryType | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesResponse, categoriesResponse] = await Promise.all([
        mockInventoryService.getTypes(),
        mockInventoryService.getCategories()
      ]);
      setTypes(typesResponse.data);
      setCategories(categoriesResponse.data.filter((cat: InventoryCategory) => cat.is_active));
    } catch (error) {
      console.error('Error loading inventory types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditingType(null);
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  const handleAddClick = () => {
    setEditingType(null);
    setFormData(initialFormData);
    setErrors({});
    setShowForm(true);
  };

  const handleEditClick = (type: InventoryType) => {
    setEditingType(type);
    setFormData({
      type_code: type.type_code,
      type_name: type.type_name,
      type_description: type.type_description || '',
      category_id: type.category_id,
      file_extensions: type.file_extensions || '',
      default_base_path: type.default_base_path || '',
      supports_versioning: type.supports_versioning,
      supports_dependencies: type.supports_dependencies,
      display_order: type.display_order,
      is_active: type.is_active
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDeleteClick = async (type: InventoryType) => {
    const ok = await confirmDelete({ title: `Delete type "${type.type_name}"?` });
    if (!ok) return;
    try {
      await mockInventoryService.deleteType(type.id);
      await loadData();
    } catch (error) {
      console.error('Error deleting inventory type:', error);
      swalError('Failed to delete type. Please try again.');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.type_code.trim()) {
      newErrors.type_code = 'Type code is required';
    } else if (formData.type_code.length > 50) {
      newErrors.type_code = 'Type code must be 50 characters or less';
    }

    if (!formData.type_name.trim()) {
      newErrors.type_name = 'Type name is required';
    } else if (formData.type_name.length > 100) {
      newErrors.type_name = 'Type name must be 100 characters or less';
    }

    if (!formData.type_description.trim()) {
      newErrors.type_description = 'Description is required';
    } else if (formData.type_description.length > 500) {
      newErrors.type_description = 'Description must be 500 characters or less';
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required';
    }

    if (formData.display_order < 1) {
      newErrors.display_order = 'Display order must be greater than 0';
    }

    // Check for duplicate codes/names (excluding current item when editing)
    const existingType = types.find(t =>
      (t.type_code.toLowerCase() === formData.type_code.toLowerCase() ||
       t.type_name.toLowerCase() === formData.type_name.toLowerCase()) &&
      t.id !== editingType?.id
    );
    if (existingType) {
      if (existingType.type_code.toLowerCase() === formData.type_code.toLowerCase()) {
        newErrors.type_code = 'A type with this code already exists';
      }
      if (existingType.type_name.toLowerCase() === formData.type_name.toLowerCase()) {
        newErrors.type_name = 'A type with this name already exists';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const requestData = {
        type_code: formData.type_code.trim(),
        type_name: formData.type_name.trim(),
        type_description: formData.type_description.trim() || undefined,
        category_id: formData.category_id,
        file_extensions: formData.file_extensions.trim() || undefined,
        default_base_path: formData.default_base_path.trim() || undefined,
        supports_versioning: formData.supports_versioning,
        supports_dependencies: formData.supports_dependencies,
        display_order: formData.display_order,
        is_active: formData.is_active,
        created_by: 1 // TODO: Get from auth context
      };

      if (editingType) {
        await mockInventoryService.updateType(editingType.id, requestData);
      } else {
        await mockInventoryService.createType(requestData);
      }

      await loadData();
      setShowForm(false);
      setEditingType(null);
      setFormData(initialFormData);
      setErrors({});
    } catch (error) {
      console.error('Error saving inventory type:', error);
      swalError('Failed to save type. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackClick = () => {
    setShowForm(false);
    setEditingType(null);
    setFormData(initialFormData);
    setErrors({});
  };

  const getCategoryName = (categoryId: number): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.category_name || 'Unknown';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Inventory Types Management</Modal.Header>
      <Modal.Body className="!p-0 overflow-hidden">
        <div className="modal-content">
          {!showForm ? (
            <div className="list-view">
              <div className="list-header">
                <div className="list-title">
                  <h3>Manage Inventory Types</h3>
                  <p>Define and organize different types of inventory items within categories</p>
                </div>
                <button
                  className="add-button"
                  onClick={handleAddClick}
                  disabled={loading}
                  data-testid="BTN_inventory_type_add"
                >
                  <span>+</span>
                  Add New Type
                </button>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading inventory types...</p>
                </div>
              ) : types.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🏷️</div>
                  <h3>No Types Found</h3>
                  <p>Start by creating your first inventory type to organize your project components.</p>
                  <button className="create-button" onClick={handleAddClick} data-testid="BTN_inventory_type_create_empty">
                    Create First Type
                  </button>
                </div>
              ) : (
                <div className="items-grid">
                  {types.map((type) => (
                    <div key={type.id} className="item-card">
                      {/* Header Row: Code + Name */}
                      <div className="data_card_header">
                        <div className="code-name-row">
                          <span className="item-code">{type.type_code || 'NO-CODE'}</span>
                          <span className="item-name">{type.type_name || 'NO-NAME'}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="data_default">
                        {type.type_description || 'No description provided'}
                      </div>

                      {/* Tags and Status */}
                      <div className="data_tags">
                        <span>Category: {getCategoryName(type.category_id)}</span>
                        <span> • </span>
                        <span>Status: {type.is_active ? 'Active' : 'Inactive'}</span>
                        <span> • </span>
                        <span>Versioning: {type.supports_versioning ? 'Yes' : 'No'}</span>
                        {type.file_extensions && (
                          <>
                            <span> • </span>
                            <span>Ext: {type.file_extensions}</span>
                          </>
                        )}
                      </div>

                      {/* Footer with Path and Actions */}
                      <div className="data_card_footer">
                        <div className="data_paths">
                          📁 {type.default_base_path || 'No base path specified'}
                        </div>
                        <div style={{display: 'flex', gap: '8px', marginLeft: 'auto'}}>
                          <button
                            className="edit-button"
                            onClick={() => handleEditClick(type)}
                            disabled={submitting}
                            data-testid={`BTN_inventory_type_row_${type.id}_edit`}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteClick(type)}
                            disabled={submitting}
                            data-testid={`BTN_inventory_type_row_${type.id}_delete`}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="form-view">
              <div className="form-header">
                <h3>{editingType ? 'Edit Type' : 'Add New Type'}</h3>
                <button className="back-button" onClick={handleBackClick} data-testid="BTN_inventory_type_back">
                  ← Back to List
                </button>
              </div>

              <form onSubmit={handleSubmit} className="inventory-form" data-testid="MOD_inventory_type_form">
                <div className="form-grid">
                  <div className="form-group">
                    <FormControl.Input
                      label="Type Code"
                      required
                      id="type_code"
                      name="type_code"
                      data-testid="INP_inventory_type_code"
                      value={formData.type_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, type_code: e.target.value }))}
                      error={errors.type_code}
                      disabled={submitting}
                      placeholder="e.g., REACT_COMPONENT, API_ROUTE, DB_TABLE"
                      maxLength={50}
                      helperText="Unique code identifier for this type"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Type Name"
                      required
                      id="type_name"
                      name="type_name"
                      data-testid="INP_inventory_type_name"
                      value={formData.type_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, type_name: e.target.value }))}
                      error={errors.type_name}
                      disabled={submitting}
                      placeholder="e.g., React Component, API Route, Database Table"
                      maxLength={100}
                      helperText="Enter a descriptive name for this type"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.SearchSelect
                      label="Category"
                      required
                      id="category_id"
                      data-testid="SEL_inventory_type_category"
                      value={formData.category_id || ''}
                      onChange={(v) => setFormData(prev => ({ ...prev, category_id: Number(v ?? 0) }))}
                      options={[
                        { value: 0, label: 'Select a category' },
                        ...categories.map((cat) => ({ value: cat.id, label: cat.category_name }))
                      ]}
                      error={errors.category_id}
                      disabled={submitting}
                      helperText="Select the parent category"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Display Order"
                      required
                      id="display_order"
                      name="display_order"
                      type="number"
                      data-testid="INP_inventory_type_display_order"
                      min={1}
                      value={formData.display_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 1 }))}
                      error={errors.display_order}
                      disabled={submitting}
                      helperText="Order for displaying this type"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="File Extensions"
                      id="file_extensions"
                      name="file_extensions"
                      data-testid="INP_inventory_type_file_extensions"
                      value={formData.file_extensions}
                      onChange={(e) => setFormData(prev => ({ ...prev, file_extensions: e.target.value }))}
                      disabled={submitting}
                      placeholder="e.g., .js,.jsx,.ts,.tsx"
                      helperText="Comma-separated list (e.g., .js,.jsx,.ts,.tsx)"
                    />
                  </div>

                  <div className="form-group">
                    <FormControl.Input
                      label="Default Base Path"
                      id="default_base_path"
                      name="default_base_path"
                      data-testid="INP_inventory_type_base_path"
                      value={formData.default_base_path}
                      onChange={(e) => setFormData(prev => ({ ...prev, default_base_path: e.target.value }))}
                      disabled={submitting}
                      placeholder="e.g., src/components, backend/routes"
                      helperText="Default directory path for this type"
                    />
                  </div>

                  <div className="form-group full-width">
                    <FormControl.Textarea
                      label="Description"
                      required
                      id="type_description"
                      name="type_description"
                      data-testid="INP_inventory_type_description"
                      value={formData.type_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, type_description: e.target.value }))}
                      error={errors.type_description}
                      disabled={submitting}
                      rows={4}
                      placeholder="Describe what this type represents and when to use it..."
                      maxLength={500}
                      helperText="Detailed description of this type and its purpose"
                    />
                  </div>

                  <div className="form-group full-width">
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <FormControl.Checkbox
                        label="Supports Versioning"
                        checked={formData.supports_versioning}
                        onChange={(checked) => setFormData(prev => ({ ...prev, supports_versioning: checked }))}
                        disabled={submitting}
                        data-testid="CHK_inventory_type_supports_versioning"
                      />

                      <FormControl.Checkbox
                        label="Supports Dependencies"
                        checked={formData.supports_dependencies}
                        onChange={(checked) => setFormData(prev => ({ ...prev, supports_dependencies: checked }))}
                        disabled={submitting}
                        data-testid="CHK_inventory_type_supports_dependencies"
                      />

                      <FormControl.Checkbox
                        label="Active Type"
                        checked={formData.is_active}
                        onChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        disabled={submitting}
                        data-testid="CHK_inventory_type_is_active"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="outline" type="button" onClick={handleBackClick} data-testid="BTN_inventory_type_cancel">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant={editingType ? 'update' : 'fill'}
                    icon={editingType ? Check : Save}
                    disabled={submitting}
                    data-testid="BTN_inventory_type_submit"
                  >
                    {submitting ? (editingType ? 'Updating...' : 'Submitting...') : editingType ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default InventoryTypesModal;
